import React, { useState, useEffect } from 'react';

import { useLoading, useFetchTWData, useSpreadsheets } from '../../context/stateContext';

import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {
  useFetchTWDataCardInterface,
  useFetchTWDataUserInterface,
  useFetchTWDataInterface,
} from '../../types/stateContextTypes';
import {
  FormatRequest,
  GsDataInterface,
  longCommentsInterface,
  rangeInterface,
  taskInterface,
} from '../../types/exportProjectTypes';

const SPREADSHEET_ID = '1wHz5KRB7OP7Y3_0EPuBmbL_iqEdGyIfKc_RpDLSLDpY';
const SPREADSHEET_NAME = 'Report';
const SHEET_ID = '1474553227';
const SPREADSHEET_COLUMNS = ['Who', 'Date', 'Task List', 'Time', 'Comment'];

const formatDate = (date: Date) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const initFromDate = () => {
  const date = new Date();
  const day = date.getDay();
  const daysToMonday = day === 0 ? 6 : day - 1;
  const mondayDate = new Date(date.setDate(date.getDate() - daysToMonday));
  return formatDate(mondayDate);
};

const getToDate = (from: string) => {
  const date = new Date(from);
  const day = date.getDay();
  const daysToSunday = day === 0 ? 0 : 7 - day;
  const sundayDate = new Date(date.setDate(date.getDate() + daysToSunday));
  return formatDate(sundayDate);
};

const sortObjectEntries = ([key1, value1]: [string, unknown], [key2, value2]: [string, unknown]) =>
  key1.localeCompare(key2);

const generateFormatRequest = (range: rangeInterface, bold: boolean) => {
  return {
    repeatCell: {
      range,
      cell: {
        userEnteredFormat: {
          textFormat: {
            bold,
          },
        },
      },
      fields: 'userEnteredFormat.textFormat.bold',
    },
  };
};

const generateFormatRequests = (totalRows: number, longComments: longCommentsInterface[]) => {
  // remove bold styles for all cells
  const resetFormatRequest = generateFormatRequest(
    {
      sheetId: Number(SHEET_ID),
      endRowIndex: totalRows,
    },
    false,
  );
  // add bold styles for 'long comment' cellls
  const boldFormatRequests = longComments.reduce((acc: FormatRequest[], curr) => {
    const range = {
      sheetId: Number(SHEET_ID),
      startRowIndex: curr.row,
      endRowIndex: curr.row + 1,
      startColumnIndex: curr.column,
      endColumnIndex: curr.column + 1,
    };
    const request = generateFormatRequest(range, true);
    acc.push(request);
    return acc;
  }, []);
  return [resetFormatRequest, ...boldFormatRequests];
};

const ExportProject = () => {
  const [loading] = useLoading();
  const { load } = useFetchTWData<
    useFetchTWDataInterface | useFetchTWDataCardInterface | useFetchTWDataUserInterface
  >();
  const [update, clear, format] = useSpreadsheets(SPREADSHEET_ID, SPREADSHEET_NAME);

  const [project, setProject] = useState<
    useFetchTWDataInterface | useFetchTWDataCardInterface | useFetchTWDataUserInterface | undefined
  >(undefined);

  const [fromDate, setFromDate] = useState(initFromDate);
  const [toDate, setToDate] = useState('');

  const [projects, setProjects] = useState<
    (useFetchTWDataInterface | useFetchTWDataCardInterface | useFetchTWDataUserInterface)[]
  >([]);

  const [isFormValid, setIsFormValid] = useState(false);

  const onProjectChange = (event: React.ChangeEvent<{}>, value: string) => {
    const selectedProject = projects.find((project) => 'name' in project && project.name === value);
    setProject(selectedProject);
  };

  const onFromDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFromDate(event.target.value);
  };

  const onToDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setToDate(event.target.value);
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      const dateRange = {
        fromdate: fromDate.split('-').join('').toString(),
        todate: toDate.split('-').join('').toString(),
      };
      const data = await load(
        `/projects/${project!.id}/time_entries.json`,
        { ...dateRange, pageSize: 250 },
        'time-entries',
      );
      const { updatedGsData, longComments } = mapTeamworkDataToSpreadsheetData(data);
      await clear();
      await update(updatedGsData);
      const formatRequests = generateFormatRequests(updatedGsData.length, longComments);
      await format(formatRequests);
    } catch (error) {}
  };

  const mapTeamworkDataToSpreadsheetData = (
    data: (useFetchTWDataInterface | useFetchTWDataCardInterface | useFetchTWDataUserInterface)[],
  ) => {
    let gsData: GsDataInterface = data.reduce((acc: GsDataInterface, curr) => {
      const person =
        'person-first-name' in curr && 'person-last-name' in curr
          ? `${curr['person-first-name']} ${curr['person-last-name']}`
          : '';
      const date = 'date' in curr && curr.date !== undefined ? formatDate(new Date(curr.date)) : '';
      const entry = {
        date: date,
        tasklist: 'todo-list-name' in curr ? curr['todo-list-name'] : '',
        taskTag: 'todo-item-name' in curr ? curr['todo-item-name']!.match(/^([\S]+)/)![0] : '',
        task: 'todo-item-name' in curr ? curr['todo-item-name'] : '',
        time: 'hoursDecimal' in curr ? curr.hoursDecimal : '',
        description: 'description' in curr ? curr.description : '',
      };
      const entryData = {
        time: entry.time,
        description: entry.description,
      };
      if (acc[person]) {
        const dateEntry = acc[person][entry.date];
        if (dateEntry) {
          const tasklistEntry = dateEntry[entry.tasklist || ''];
          if (tasklistEntry) {
            const taskTag = tasklistEntry[entry.taskTag || ''];
            if (taskTag) {
              taskTag.push(entryData);
            } else {
              tasklistEntry[entry.taskTag] = [entryData];
            }
          } else {
            dateEntry[entry.tasklist || ''] = {
              [entry.taskTag]: [entryData],
            };
          }
        } else {
          acc[person][entry.date] = {
            [entry.tasklist || '']: {
              [entry.taskTag]: [entryData],
            },
          };
        }
      } else {
        acc[person] = {
          [entry.date]: {
            [entry.tasklist || '']: {
              [entry.taskTag]: [entryData],
            },
          },
        };
      }
      return acc;
    }, {});
    const longComments: { row: number; column: number }[] = [];

    let updatedGsData = Object.entries(gsData)
      .sort(sortObjectEntries)
      .reduce((data: (string | number)[][], [person, dates]) => {
        // new row for person
        data.push([person]);
        Object.entries(dates)
          .sort(sortObjectEntries)
          .forEach(([date, tasklists]) => {
            // new row for date
            data.push(['', date]);
            Object.entries(tasklists)
              .sort(sortObjectEntries)
              .forEach(([tasklist, taskTags]) => {
                const { tasklistTime, tasklistDesc } = Object.entries(taskTags).reduce(
                  ({ tasklistTime, tasklistDesc }, [tag, tasks]) => {
                    const { taskTime, taskDesc } = (tasks as Array<taskInterface>).reduce(
                      ({ taskTime, taskDesc }, task) => {
                        taskTime = Number((taskTime + Number(task.time)).toFixed(2));
                        if (task.description) {
                          taskDesc += `${task.description}, `;
                        }
                        return { taskTime, taskDesc };
                      },
                      { taskTime: 0, taskDesc: '' },
                    );
                    tasklistTime = Number((tasklistTime + taskTime).toFixed(2));
                    const formattedTaskTime = `(${taskTime.toFixed(2)})`.replace('.', ',');
                    const formattedTaskDesc = `${taskDesc.slice(0, taskDesc.length - 2)}; `;
                    tasklistDesc = `${tasklistDesc}${tag} ${formattedTaskTime} ${formattedTaskDesc}`;
                    return { tasklistTime, tasklistDesc };
                  },
                  { tasklistTime: 0, tasklistDesc: '' },
                );
                const comment = `${tasklistDesc.slice(0, tasklistDesc.length - 2)}`;
                const rowData = ['', '', tasklist, tasklistTime, comment];
                if (comment.length >= 400) {
                  longComments.push({ row: data.length + 1, column: rowData.length - 1 });
                }
                // new row for data
                data.push(rowData);
              });
          });
        return data;
      }, []);
    updatedGsData.unshift(SPREADSHEET_COLUMNS);
    return { updatedGsData, longComments };
  };

  useEffect(() => {
    async function fetchProjects() {
      try {
        let data = await load('projects.json', { pageSize: 500 }, 'projects');
        setProjects(data);
      } catch (error) {
        setProjects([]);
      }
    }
    fetchProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const toDate = getToDate(fromDate);
    setToDate(toDate);
  }, [fromDate]);

  useEffect(() => {
    if (!project) {
      setIsFormValid(false);
    } else {
      try {
        const from = new Date(fromDate);
        const to = new Date(toDate);
        const isValid = from.getTime() <= to.getTime();
        setIsFormValid(isValid);
      } catch (error) {
        setIsFormValid(false);
      }
    }
  }, [fromDate, toDate, project, setIsFormValid]);

  return (
    <form
      onSubmit={onSubmit}
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        marginTop: '4rem',
      }}
    >
      <Autocomplete
        id="project"
        options={projects}
        getOptionLabel={(project) => ('name' in project ? project.name : '')}
        style={{ width: '100%', maxWidth: '240px' }}
        onInputChange={onProjectChange}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Project"
            label="Project"
          />
        )}
      />
      <TextField
        id="from"
        type="date"
        label="From"
        value={fromDate}
        onChange={onFromDateChange}
        style={{ width: '100%', maxWidth: '240px', marginTop: '1rem' }}
      />
      <TextField
        id="to"
        type="date"
        label="To"
        value={toDate}
        onChange={onToDateChange}
        style={{ width: '100%', maxWidth: '240px', marginTop: '1rem' }}
      />
      <Button
        type="submit"
        color="primary"
        variant="contained"
        disabled={!isFormValid || (loading as boolean)}
        style={{ marginTop: '2rem' }}
      >
        Export
      </Button>
    </form>
  );
};

export default ExportProject;
